import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, TextField, Typography,
} from '@mui/material';
import api from '../services/api';

const reais = (valor) => Number(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const quando = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '');
const TIPO = {
  inicial: { rotulo: 'Venda inicial', cor: '#13375F', fundo: '#E3ECF6' },
  aumento: { rotulo: 'Aumento', cor: '#3B6D11', fundo: '#EAF3DE' },
  reducao: { rotulo: 'Redução', cor: '#A32D2D', fundo: '#FCEBEB' },
};

// Lista dos valores que o contrato já teve (venda inicial, aditivos, correções).
function HistoricoContrato({ itens }) {
  if (!itens || itens.length === 0) return null;
  return (
    <Box sx={{ mt: 2.5 }}>
      <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>Histórico do valor</Typography>
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, maxHeight: 220, overflowY: 'auto' }}>
        {itens.map((h) => {
          const t = TIPO[h.tipo] || TIPO.inicial;
          return (
            <Box component="li" key={h.id} sx={{ py: 1, borderTop: '1px solid #e8edf3' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={t.rotulo} size="small" sx={{ backgroundColor: t.fundo, color: t.cor, fontWeight: 700, height: 22 }} />
                <Typography sx={{ fontSize: 13.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                  {h.valor_anterior !== null && h.valor_anterior !== undefined ? `${reais(h.valor_anterior)} → ` : ''}{reais(h.valor_novo)}
                </Typography>
              </Box>
              {h.motivo && <Typography sx={{ fontSize: 13, mt: 0.25 }}>{h.motivo}</Typography>}
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                {quando(h.criado_em)}{h.alterado_por_nome ? ` por ${h.alterado_por_nome}` : ''}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// Janela para informar ou corrigir o valor total do contrato de um projeto.
// Usada na tela Comercial e na tela Financeiro (quem tem esses módulos pode alterar).
export default function EditarContratoDialog({ projeto, onFechar, onSalvo }) {
  const [valor, setValor] = useState('');
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (projeto) {
      setValor(projeto.valor_contrato ?? '');
      setMotivo('');
      setErro('');
    }
  }, [projeto]);

  const jaTinha = projeto && projeto.valor_contrato !== null && projeto.valor_contrato !== undefined && projeto.valor_contrato !== '';

  const salvar = async () => {
    if (!valor || Number(valor) <= 0) {
      setErro('Informe um valor maior que zero.');
      return;
    }
    setSalvando(true);
    setErro('');
    try {
      const res = await api.patch(`/projetos/${projeto.id}/`, {
        valor_contrato: String(valor),
        motivo_valor_contrato: motivo.trim(),
      });
      onSalvo(res.data);
    } catch (err) {
      const dados = err?.response?.data;
      const doCampo = dados?.valor_contrato;
      setErro((Array.isArray(doCampo) ? doCampo[0] : doCampo) || dados?.detail || 'Não foi possível salvar o valor do contrato.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={Boolean(projeto)} onClose={salvando ? undefined : onFechar} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>{jaTinha ? 'Alterar valor do contrato' : 'Informar valor do contrato'}</DialogTitle>
      <DialogContent>
        {projeto && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {projeto.nome}{projeto.cliente_nome ? ` — ${projeto.cliente_nome}` : ''}
          </Typography>
        )}
        {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}
        <TextField
          autoFocus fullWidth required type="number" label="Valor total do contrato"
          value={valor}
          onChange={(e) => setValor(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') salvar(); }}
          slotProps={{ input: { startAdornment: <InputAdornment position="start">R$</InputAdornment> }, htmlInput: { min: 0, step: '0.01' } }}
          helperText={
            jaTinha
              ? `Valor atual: ${reais(projeto.valor_contrato)}. Se houve aditivo, informe o novo valor total.`
              : 'Valor total da venda, em reais.'
          }
        />
        <TextField
          fullWidth size="small" label="Motivo da alteração (opcional)" sx={{ mt: 2 }}
          value={motivo} onChange={(e) => setMotivo(e.target.value)}
          placeholder="Ex.: Aditivo 01, correção de digitação"
          slotProps={{ htmlInput: { maxLength: 300 } }}
        />
        <HistoricoContrato itens={projeto?.historico_contrato} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onFechar} disabled={salvando}>Cancelar</Button>
        <Button variant="contained" onClick={salvar} disabled={salvando} sx={{ backgroundColor: '#13375F', textTransform: 'none', fontWeight: 600 }}>
          {salvando ? 'Salvando...' : 'Salvar valor'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
